import { useContext } from 'react'
import { generatePath } from 'react-router-dom'
import { CompanyTag } from '@/components/ui/badge'
import { GridCellProps, GridColumnProps } from '@progress/kendo-react-grid'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { AdjustmentTransactionKey } from 'api/Transaction/Types/AdjustmentTransactionDto'
import { Link } from 'components/Link'
import { generateClassNameForReverseType } from 'helpers/transactions'
import { AuthSessionContext } from 'utils/AuthContext'
import { formatLocalDateUTC } from 'utils/date'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { hasPermission } from 'utils/permissionsValidator'

import './Create/transaction.scss'

export interface GridColumn extends GridColumnProps {
  show: boolean
  width?: number
}

const ReferenceCell = (props: GridCellProps): JSX.Element => {
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  if (!hasPermission(permissions, PermissionsEnum.CanViewWalletTradingAccountTransaction)) {
    return (
      <td className={generateClassNameForReverseType(props.dataItem)}>
        {props.dataItem.referenceId}
      </td>
    )
  }

  return (
    <td
      className={generateClassNameForReverseType(props.dataItem)}
      style={{ color: 'var(--link)', textDecoration: 'underline' }}
    >
      <Link
        to={{
          pathname: generatePath('/transactions/:id', {
            id: props.dataItem.id,
          }),
        }}
      >
        {props.dataItem.referenceId}
      </Link>
    </td>
  )
}

const walletsTransactionAccountColumns: GridColumn[] = [
  {
    title: 'Reference',
    field: AdjustmentTransactionKey.reference,
    cell: ReferenceCell,
    filter: 'text',
    sortable: false,
    show: true,
  },
  {
    title: 'Type',
    field: AdjustmentTransactionKey.type,
    cell: (props: GridCellProps): JSX.Element => {
      const type = props.dataItem?.type
      if (isEmpty(type)) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      return <td className={generateClassNameForReverseType(props.dataItem)}>{type.name}</td>
    },
    filter: 'text',
    sortable: false,
    show: true,
  },
  {
    title: 'Client',
    field: AdjustmentTransactionKey.client,
    cell: (props: GridCellProps): JSX.Element => {
      const client = props.dataItem?.client
      if (isEmpty(client)) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      return (
        <td
          className={generateClassNameForReverseType(props.dataItem)}
          style={{ color: 'var(--link)', textDecoration: 'underline' }}
        >
          <Link
            to={{
              pathname: generatePath(links.recordsClients.detailsUrl as string, {
                id: client.id,
                slug: 'details',
              }),
            }}
            state={{ redirectTo: links.transactionPlatform.url }}
          >
            {client.name}
          </Link>
        </td>
      )
    },
    filter: 'text',
    sortable: false,
    show: true,
  },
  {
    title: 'Wallet',
    field: AdjustmentTransactionKey.wallet,
    cell: (props: GridCellProps): JSX.Element => {
      const wallet = props.dataItem?.wallet
      if (isEmpty(wallet)) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      return (
        <td
          className={generateClassNameForReverseType(props.dataItem)}
          style={{ color: 'var(--link)', textDecoration: 'underline' }}
        >
          <Link
            to={{
              pathname: generatePath(links.recordsWallets.detailsUrl as string, {
                id: wallet.id,
                slug: 'details',
              }),
            }}
            state={{ redirectTo: links.transactionPlatform.url }}
          >
            {wallet.name}
          </Link>
        </td>
      )
    },
    filter: 'text',
    resizable: true,
    sortable: false,
    show: true,
  },
  {
    title: 'Trading Account',
    field: AdjustmentTransactionKey.tradingAccount,
    cell: (props: GridCellProps): JSX.Element => {
      const tradingAccount = props.dataItem?.tradingAccount
      if (!tradingAccount) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      return (
        <td
          className={generateClassNameForReverseType(props.dataItem)}
          style={{ color: 'var(--link)', textDecoration: 'underline' }}
        >
          <Link
            to={{
              pathname: generatePath(links.recordsTradingAccounts.detailsUrl as string, {
                id: tradingAccount.id,
                slug: 'details',
              }),
            }}
            state={{ redirectTo: links.transactionPlatform.url }}
          >
            {tradingAccount.name}
          </Link>
        </td>
      )
    },
    filter: 'text',
    resizable: true,
    sortable: false,
    show: true,
  },
  {
    title: 'Platform',
    field: AdjustmentTransactionKey.platformType,
    cell: (props: GridCellProps): JSX.Element => {
      const tradingAccountPlatformType = props.dataItem?.tradingAccountPlatformType
      if (isEmpty(tradingAccountPlatformType)) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      return (
        <td className={generateClassNameForReverseType(props.dataItem)}>
          {tradingAccountPlatformType.name}
        </td>
      )
    },
    filter: 'text',
    sortable: false,
    show: true,
  },
  {
    title: 'Status',
    field: AdjustmentTransactionKey.state,
    cell: (props: GridCellProps): JSX.Element => {
      const status = props.dataItem?.state
      if (isEmpty(status)) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      return <td className={generateClassNameForReverseType(props.dataItem)}>{status.name}</td>
    },
    filter: 'text',
    sortable: false,
    show: true,
  },
  {
    title: 'Amount',
    field: AdjustmentTransactionKey.amount,
    cell: (props: GridCellProps): JSX.Element => {
      const amount = props.dataItem?.amount
      if (isEmpty(amount)) {
        return <td className={generateClassNameForReverseType(props.dataItem, 'number')} />
      }

      return (
        <td className={generateClassNameForReverseType(props.dataItem, 'number')}>
          {amount.toFixed(2)}
        </td>
      )
    },
    filter: 'text',
    resizable: true,
    sortable: false,
    show: true,
  },
  {
    title: 'Currency',
    field: AdjustmentTransactionKey.currency,
    cell: (props: GridCellProps): JSX.Element => {
      const currencyId = props.dataItem?.currency?.id
      if (isEmpty(currencyId)) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      return <td className={generateClassNameForReverseType(props.dataItem)}>{currencyId}</td>
    },
    filter: 'text',
    resizable: true,
    sortable: false,
    show: true,
  },
  {
    title: 'Entity',
    field: AdjustmentTransactionKey.tickmillCompany,
    cell: (props: GridCellProps): JSX.Element => {
      const company = props.dataItem.tickmillCompany
      if (!company) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      return (
        <td className={generateClassNameForReverseType(props.dataItem)}>
          <CompanyTag id={company.id}>{company.name}</CompanyTag>
        </td>
      )
    },
    filter: 'text',
    width: 80,
    minResizableWidth: 80,
    resizable: true,
    sortable: false,
    show: true,
  },
  {
    title: 'Date Created',
    field: AdjustmentTransactionKey.createdDate,
    cell: (props: GridCellProps) => (
      <td className={generateClassNameForReverseType(props.dataItem, 'date')}>
        {formatLocalDateUTC(props.dataItem.createdDate, true)}
      </td>
    ),
    width: 150,
    filter: 'text',
    resizable: true,
    sortable: false,
    show: true,
  },
  {
    title: 'Date Updated',
    field: AdjustmentTransactionKey.updatedDate,
    cell: (props: GridCellProps) => (
      <td className={generateClassNameForReverseType(props.dataItem, 'date')}>
        {formatLocalDateUTC(props.dataItem.updatedDate, true)}
      </td>
    ),
    width: 150,
    filter: 'text',
    resizable: true,
    sortable: false,
    show: true,
  },
]

export default walletsTransactionAccountColumns
